<script lang="ts">
  import { ResponsiveVars, TwistDivider } from '@twist/core';
  import type { LegalCountry, LegalLink } from '$lib/sanity/schemas';
  import LegalLinkGroupWrapper from './LegalLinkGroupWrapper.svelte';
  import { EntityType } from '$lib/sanity/types';
  import {
    useEncodeDataAttribute,
    type ContentSourceMap,
    type StudioPathLike
  } from '@sanity/svelte-loader';
  import { sanityStudioURL } from '$lib/client';

  export let document: LegalCountry;
  export let path: StudioPathLike = [];
  export let sourceMap: ContentSourceMap | undefined = undefined;

  const groupLinks = (documentLinks: LegalLink[]) => {
    // If link does not have a language group, map it under empty string
    let linkGroups: Record<string, LegalLink[]> = { '': [] };

    for (let link of documentLinks) {
      const key = link.languageGroup ?? '';

      if (!(key in linkGroups)) {
        linkGroups[key] = [];
      }

      linkGroups[key].push(link);
    }

    return linkGroups;
  };

  function hrefFrom(link: LegalLink) {
    if (link.type === 'url') {
      return link.url;
    }

    if (link.page?._type === EntityType.LegalLicensesPage) {
      return '/legal/licenses';
    }

    return `/legal/${link.page?.slug}`;
  }

  $: linkGroups = groupLinks(document.links);

  const responsiveVars = new ResponsiveVars()
    .addFluid('country-name-font-size', [
      ['min', 18],
      ['lg', 'max', 18, 24],
      ['max', 24]
    ])
    .addFluid('margin-top', [
      ['min', 48],
      ['lg', 'max', 48, 96],
      ['max', 96]
    ])
    .addFluid('margin-bottom', [
      ['min', 14],
      ['lg', 'max', 14, 32],
      ['max', 32]
    ])
    .addFluid('li-font-size', [
      ['min', 14],
      ['lg', 'max', 14, 16],
      ['max', 16]
    ])
    .addFluid('li-margin', [
      ['min', 16],
      ['lg', 'max', 16, 24],
      ['max', 24]
    ])
    .toCSS();

  $: encodeDataAttribute = useEncodeDataAttribute(undefined, sourceMap, sanityStudioURL);
</script>

<div class={responsiveVars}>
  <h2 class="countryName" data-sanity={encodeDataAttribute([...path, 'name'])}>
    {document.name}
  </h2>

  <TwistDivider thickness="1px" margin={0} />

  <ul>
    {#each Object.keys(linkGroups) as linkGroupKey}
      <LegalLinkGroupWrapper language={linkGroupKey}>
        {#each linkGroups[linkGroupKey] as link, linkIndex}
          <li>
            <a
              class="link"
              href={hrefFrom(link)}
              target={link.type === 'url' ? '_blank' : undefined}
              data-sanity={encodeDataAttribute([...path, 'links', linkIndex, 'copy'])}
            >
              {link.copy}
            </a>
          </li>
        {/each}
      </LegalLinkGroupWrapper>
    {/each}
  </ul>
</div>

<style lang="scss">
  .countryName {
    color: var(--twist-color-gray-100);
    font-family: var(--font-family-primary);
    font-size: var(--country-name-font-size);
    font-weight: 700;
    line-height: 100%;
    margin: var(--margin-top) 0 var(--margin-bottom);
  }

  ul {
    list-style: none;
    padding: 0;
  }

  li {
    font-family: var(--font-family-primary);
    font-size: var(--li-font-size);
    font-weight: 400;
    line-height: 150%;
    margin: var(--li-margin) 0;

    .link {
      color: var(--twist-color-gray-100);

      &:hover,
      &:focus-visible {
        color: var(--twist-color-gray-0);
      }
    }
  }
</style>
